type FormatDate = (value: Date | number, options: any) => string;

interface HumanTimeOptions {
    locale?: string;
    relative_unit?: string;
    relative_maxValue?: number;
    relative_shorten?: boolean;
    format?: any;
    separators?: string[];
    casing?: string;
}

const relative_shortenRegexp = new RegExp('^(for|för)\\s+', 'i'); // sv, da, no
/**
 * @property {Date | number} value has to be a number if `options.relative_unit` is set
 * @property {string} locale
 * @property {
 *    year | month | day | hour | minute | second: `'numeric' | 'short' | 'long'`,
 *    casing?: `'uppercase' | 'lowercase' | 'capitalized' = 'capitalized'`
 *    relative_unit?: `'year' | 'quarter' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second' = null`,
 *    relative_maxValue: `number`
 *    relative_shorten: `boolean`
 * } options
 * */
export const formatDate: FormatDate = (value: Date | number, options: any) => {
    let str = options.relative_unit
        ? new Intl.RelativeTimeFormat(options.locale, options).format((value as number) * -1, options.relative_unit)
        : new Intl.DateTimeFormat(options.locale, options).format(value);

    if (options.relative_shorten) str = str.replace(relative_shortenRegexp, '');

    switch (options.casing) {
        case 'uppercase':
            return str.toUpperCase();
        case 'lowercase':
            return str.toLowerCase();
        case 'capitalized':
        default:
            return str[0].toUpperCase() + str.slice(1);
    }
};

/** m: milliseconds */
const timeUnit: { [k: string]: { k: string; m: number; l: number; n: string | null } } = {
    year: {
        k: 'year',
        m: 31_536_000_000,
        l: 0,
        n: 'month',
    },
    month: {
        k: 'month',
        m: 604_800_000,
        l: 12,
        n: 'week',
    },
    week: {
        k: 'week',
        m: 604_800_000,
        l: 4,
        n: 'day',
    },
    day: {
        k: 'day',
        m: 86_400_000,
        l: 7,
        n: 'hour',
    },
    hour: {
        k: 'hour',
        m: 3_600_000,
        l: 24,
        n: 'minute',
    },
    minute: {
        k: 'minute',
        m: 60_000,
        l: 60,
        n: 'second',
    },
    second: {
        k: 'second',
        m: 1000,
        l: 60,
        n: null,
    },
};

export default class HumanTime {
    defaultOptions: HumanTimeOptions = {
        locale: 'en',
        relative_unit: 'week',
        relative_maxValue: 1, // 0 = timeUnit[unit].l, -1 = no relative formatting
        relative_shorten: true,
        format: [{ month: 'long', casing: 'capitalized' }, { day: 'numeric' }, { year: 'numeric' }],
        separators: [' ', ', '],
        casing: 'capitalized',
    };

    constructor(options?: HumanTimeOptions) {
        options && Object.assign(this.defaultOptions, options);
    }

    format(date: Date, options: any = this.defaultOptions) {
        let format: any = options.format,
            separators: string[] = options.separators,
            format_len = format.length,
            last_separator = separators[separators.length - 1];

        return format.reduce(
            (acc: string, opt: any, idx: number) =>
                acc +
                formatDate(date, ((opt.casing = opt.casing || options.casing), (opt.locale = opt.locale || options.locale), opt)) +
                (format_len - 1 !== idx ? (typeof separators[idx] === 'string' ? separators[idx] : last_separator) : ''),
            ''
        );
    }

    formatRelative(value: number, options: any = this.defaultOptions) {
        return formatDate(value, options);
    }

    parseAbs(value?: string | number | Date | null, locale = this.defaultOptions.locale) {
        if (!value) return;

        value = Date.parse(value as string);

        if (isNaN(value)) throw value + ' is not a valid date';

        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', hourCycle: 'h24' }).format(value);
    }

    // TODO: possibility to add & substract to given date/time
    parse(date: string | number | Date, options: any = this.defaultOptions) {
        date = Date.parse(date as string);

        if (isNaN(date)) throw date + ' is not a valid date';

        let millis: number = Date.now() - date,
            unit = timeUnit[options.relative_unit],
            interval = ~~(millis / unit.m);

        if (interval > (options.relative_maxValue || unit.l))
            return this.format(new Date(date), {
                locale: options.locale,
                format: options.format,
                separators: options.separators,
                casing: 'capitalized',
            });

        while ((interval = ~~(millis / unit.m)) === 0 && unit.n) {
            unit = timeUnit[unit.n];
        }

        return this.formatRelative(interval || 1, {
            locale: options.locale,
            relative_unit: unit.k,
            relative_maxValue: unit.l,
            relative_shorten: options.relative_shorten,
            casing: 'capitalized',
        });
    }
}
